<template>
  <div>
    <template v-if="advancedFilterNotConfigured">
      <button v-if="parentErrorText" class="grey btn-link py-2" disabled>
        <icon class="filter-full"></icon>
        Advanced filter ({{parentErrorText}})
      </button>

      <button v-else type="button" class="btn-link blue py-2" @click="showAdvancedFilterBuilder">
        <icon class="filter-full"></icon>
        Advanced filter
      </button>
    </template>

    <template v-else-if="parentErrorText">
      <label>Advanced filter</label>
      <p class="error-text m-0 font-weight-normal">{{ parentErrorText }}</p>
    </template>

    <template v-else>
      <hr v-if="showSectionDivider">
      <row>
        <label for="show-advanced-filter" class="mr-auto">Advanced filter</label>

        <button type="button" class="btn-link blue px-2" @click="editAdvancedFilter">
          <icon class="pencil"></icon>
          Edit filter
        </button>

        <button type="button" class="btn-link blue px-2" @click="removeAdvancedFilter">
          <icon class="trash"></icon>
          Remove filter
        </button>
      </row>

      <template v-if="validationErrorMessage">
        <p class="error-text font-weight-normal">{{ validationErrorMessage }}</p>
      </template>
      <row v-else>
        <label for="toggle-advanced-filter" class="sr-only">On/Off Toggle</label>
        <div>
          <input v-model="advancedFilter.enabled" id="toggle-advanced-filter" type="checkbox" class="toggle-btn">
        </div>

        <p name="advanced-filter-show">
          Only show items if <span v-html="advancedFilterRuleSet.toHtmlString()"></span>
        </p>
      </row>
    </template>

    <single-set-conditional-display-modal
        v-if="advancedFilterRuleSet"
        :state="modalStateFor(vueModalID.conditionalDisplay)"
        :index="vueModalID.conditionalDisplay"
        :key="advancedFilterRuleSet.client_id"
        :parent="advancedFilter"
        :rule-set="advancedFilterRuleSet"
        :conditional-rule-options="conditionalRuleOptions"
        hide-preview
        v-bind="conditionalDisplayModalTexts"
        @toggle-modal="hideModal(vueModalID.conditionalDisplay)"
    ></single-set-conditional-display-modal>
  </div>
</template>

<script lang="ts">
import {mapActions} from "vuex";
import Vue from 'vue';
import ModalComponentMixin from "~/mixins/modalComponentMixin";
import AdvancedFilter from "../../../models/advancedFilter";
import SingleSetConditionalDisplayModal from "./single-set-conditional-display-modal.vue";
import IAdvancedFilterable from "~/interfaces/advancedFilterInterfaces";
import {IConditionalRuleOptions} from "~/interfaces/modeling/conditionalRuleInterfaces";
import advancedFilterStoreFactory, {MUTATIONS} from "../../../store/modules/advancedFilterStoreFactory";
import RouterMixin from "~/mixins/routerMixin";
import {Hash} from "~/interfaces/hashInterfaces";
import ClearListViewFiltersEventBus from "../../../../modelViews/buses/clearListViewFiltersEventBus";
import {StringHelper} from "~/helpers/stringHelper";

export default Vue.extend({
  mixins: [
    ModalComponentMixin,
    RouterMixin,
  ],
  components: {
    SingleSetConditionalDisplayModal,
  },
  props: {
    referencableControlContainer: {
      type: Object as () => IAdvancedFilterable,
      required: true,
    },
    showSectionDivider: {
      type: Boolean,
      default: true,
    },
    parentErrorText: {
      type: String,
      default: null,
    },
  },
  data(): object {
    return {
      advancedFilter: new AdvancedFilter(this.referencableControlContainer),
      advancedFilterRuleSet: null,
    };
  },
  created() {
    this.registerClearFiltersListener();
  },
  async mounted() {
    this.initStore();
    await this.initAdvancedFilter();
    this.initAdvancedFilterRuleSet();
    this.validate();
  },
  beforeDestroy() {
    this.unregisterStore();
    this.unregisterClearFiltersListener();
    this.appendQuery({}, {clear: [AdvancedFilter.routeQueryKey]});
  },
  computed: {
    currentRouteQueryValue(): string {
      return this.routeQueryValue(AdvancedFilter.routeQueryKey);
    },
    advancedFilterNotConfigured(): boolean {
      return !this.advancedFilterRuleSet?.isComplete;
    },
    conditionalRuleOptions(): IConditionalRuleOptions {
      return {
        includeBuiltInProperties: true,
      };
    },
    friendlyReferencableControlsName(): string {
      return this.advancedFilter.friendlyReferencableControlsName;
    },
    conditionalDisplayModalTexts(): Hash<string> {
      const pluralControlsName = StringHelper.pluralize(this.friendlyReferencableControlsName, 2, false);

      return {
        header: "Edit advanced filter",
        description: `Apply a filter to the data by adding a rule with indexed ${pluralControlsName}.`,
        contextText: "Only show items",
        configCompleteText: "Complete",
        flashMessages: "Please complete the rule to save the advanced filter.",
      };
    },
    advancedFilterIsInvalid(): boolean {
      return !this.advancedFilter.isValid;
    },
    validationErrorMessage(): string {
      if (this.advancedFilterIsInvalid) {
        return StringHelper.capitalize(`${this.friendlyReferencableControlsName}/value no longer exists, please update the advanced filter.`);
      }
    },
  },
  methods: {
    initStore(): void {
      this.$store.registerModule(AdvancedFilter.storeName, advancedFilterStoreFactory(this.advancedFilter));
    },
    initAdvancedFilter(): Promise<any> {
      if (!this.currentRouteQueryValue) return Promise.resolve();

      return this.findAdvancedFilter({id: this.currentRouteQueryValue}).catch(console.error);
    },
    initAdvancedFilterRuleSet(): void {
      this.advancedFilterRuleSet = this.advancedFilter.ruleSet || this.advancedFilter.createNewFilterRuleSet();
    },
    validate(): void {
      this.advancedFilter.validate();
      this.disableIfInvalid();
    },
    handleDisabledByParent(): void {
      if (this.parentErrorText !== null) {
        this.advancedFilter.disable();
      }
    },
    registerClearFiltersListener(): void {
      ClearListViewFiltersEventBus.onEvent(this.advancedFilter.disable);
    },
    unregisterStore(): void {
      this.$store.unregisterModule(AdvancedFilter.storeName);
    },
    unregisterClearFiltersListener(): void {
      ClearListViewFiltersEventBus.offEvent(this.advancedFilter.disable);
    },
    showAdvancedFilterBuilder(): void {
      this.showModal(this.vueModalID.conditionalDisplay);
    },
    editAdvancedFilter(): void {
      this.advancedFilterRuleSet.triggerChanges = false;
      this.showAdvancedFilterBuilder();
    },
    removeAdvancedFilter(): void {
      this.destroyAdvancedFilter().then(() => {
        this.resetFilterAndRuleSet();
        this.setFilterRoute();
      });
    },
    resetFilterAndRuleSet(): void {
      this.advancedFilter = new AdvancedFilter(this.referencableControlContainer);
      this.initAdvancedFilterRuleSet();

      this.$store.commit(
          `${AdvancedFilter.storeName}/${MUTATIONS.setAdvancedFilter}`,
          {advancedFilter: this.advancedFilter},
      );
    },
    setFilterRoute(): void {
      this.appendQuery(
          {[AdvancedFilter.routeQueryKey]: this.advancedFilter.id},
          {mode: 'replace'},
      );
    },
    disableIfInvalid(): void {
      if (this.advancedFilterIsInvalid) {
        this.advancedFilter.disable();
      }
    },
    ...mapActions(AdvancedFilter.storeName, {
      findAdvancedFilter: 'find',
      destroyAdvancedFilter: 'destroy',
    }),
  },
  watch: {
    'advancedFilter.id': function (newVal) {
      if (newVal) this.setFilterRoute();
    },
    'advancedFilter.isValid': function (newVal, oldVal) {
      if (oldVal === false && newVal) {
        this.advancedFilter.enabled = newVal;
      }
    },
    parentErrorText() {
      this.handleDisabledByParent();
    },
  },
});
</script>